import { Trash2, Reply } from 'lucide-react'
import type { TaskComment } from '@/lib/sharedTypes'

interface CommentItemProps {
  comment: TaskComment
  isOwn: boolean
  isReply?: boolean
  onDelete: () => void
  onReply?: () => void
}

function formatRelativeTime(isoString: string): string {
  const then = new Date(isoString).getTime()
  if (isNaN(then)) return ''

  const diff = Date.now() - then

  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return '방금'
  if (minutes < 60) return `${minutes}분 전`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}시간 전`

  const d = new Date(isoString)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

function renderTextWithMentions(text: string) {
  const parts = text.split(/(@\S+)/g)
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} className="text-primary-500 font-medium">
          {part}
        </span>
      )
    }
    return part
  })
}

export function CommentItem({ comment, isOwn, isReply, onDelete, onReply }: CommentItemProps) {
  return (
    <div className="flex items-start gap-2.5 p-2 rounded-lg hover-surface transition-colors group">
      {/* Avatar */}
      {comment.userPhotoURL ? (
        <img
          src={comment.userPhotoURL}
          alt={comment.userName}
          className="w-7 h-7 rounded-full flex-shrink-0 object-cover"
        />
      ) : (
        <div className="w-7 h-7 rounded-full flex-shrink-0 bg-surface-tertiary flex items-center justify-center">
          <span className="text-xs font-medium text-caption">
            {comment.userName.charAt(0)}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-heading">
            {comment.userName}
          </span>
          <span className="text-xs text-disabled">
            {formatRelativeTime(comment.createdAt)}
          </span>
        </div>
        <p className="text-sm text-body mt-0.5 whitespace-pre-wrap break-words">
          {renderTextWithMentions(comment.text)}
        </p>

        {/* Inline image */}
        {comment.imageUrl && (
          <img
            src={comment.imageUrl}
            alt="comment image"
            className="mt-1.5 rounded-lg max-w-[240px] max-h-[180px] object-cover cursor-pointer"
            style={{
              aspectRatio: `${comment.imageWidth || 300} / ${comment.imageHeight || 200}`,
            }}
            loading="lazy"
          />
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all">
        {onReply && !isReply && (
          <button
            onClick={onReply}
            className="p-0.5 rounded text-disabled hover:text-primary-500"
          >
            <Reply className="w-4 h-4" />
          </button>
        )}
        {isOwn && (
          <button
            onClick={onDelete}
            className="p-0.5 rounded text-disabled hover:text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}
